import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { translations } from '../i18n/translations';

const DEFAULT_LANG = 'it';
const STORAGE_KEY = 'app_language';
const SUPPORTED_LANGS = Object.keys(translations);

const LanguageContext = createContext({
  lang: DEFAULT_LANG,
  setLang: () => {},
  t: (key) => key,
});

const getInitialLang = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && translations[saved]) {
      return saved;
    }
  } catch (error) {
    console.warn('Cannot read language from localStorage', error);
  }
  try {
    if (typeof navigator !== 'undefined') {
      const browserLocales = Array.isArray(navigator.languages) && navigator.languages.length > 0
        ? navigator.languages
        : [navigator.language];

      const normalized = browserLocales
        .filter(Boolean)
        .map((locale) => String(locale).toLowerCase());

      const matchedCode = SUPPORTED_LANGS.find((code) =>
        normalized.some((locale) => locale === code || locale.startsWith(`${code}-`))
      );

      if (matchedCode) {
        return matchedCode;
      }

      if (normalized.length > 0) {
        const hasItalianLocale = normalized.some((locale) => locale === 'it' || locale.startsWith('it-'));
        return hasItalianLocale ? 'it' : 'en';
      }
    }
  } catch (error) {
    console.warn('Cannot detect browser language', error);
  }

  try {
    const timezone = Intl?.DateTimeFormat?.().resolvedOptions?.().timeZone;
    if (timezone === 'Europe/Rome') {
      return 'it';
    }
  } catch (error) {
    console.warn('Cannot detect timezone for language fallback', error);
  }

  return 'en';
};

const readStoredLang = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && translations[saved]) {
      return saved;
    }
  } catch (error) {
    console.warn('Cannot read language from localStorage', error);
  }
  return null;
};

const getValueByKeyPath = (obj, keyPath) =>
  keyPath.split('.').reduce((acc, part) => (acc && acc[part] != null ? acc[part] : undefined), obj);

const applyParams = (template, params) => {
  if (!params) return template;
  return template.replace(/\{\{(\w+)\}\}/g, (_, key) => String(params[key] ?? ''));
};

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(getInitialLang);

  const setLang = (nextLang) => {
    if (!translations[nextLang]) return;
    setLangState(nextLang);
    try {
      localStorage.setItem(STORAGE_KEY, nextLang);
    } catch (error) {
      console.warn('Cannot save language to localStorage', error);
    }
  };

  const t = useCallback((key, params) => {
    const dict = translations[lang] || translations[DEFAULT_LANG];
    const fallbackDict = translations[DEFAULT_LANG];
    const value = getValueByKeyPath(dict, key) ?? getValueByKeyPath(fallbackDict, key);
    if (typeof value !== 'string') return key;
    return applyParams(value, params);
  }, [lang]);

  useEffect(() => {
    const syncLangFromStorage = () => {
      const next = readStoredLang();
      if (next && next !== lang) {
        setLangState(next);
      }
    };

    window.addEventListener('storage', syncLangFromStorage);
    window.addEventListener('app-language-updated', syncLangFromStorage);
    return () => {
      window.removeEventListener('storage', syncLangFromStorage);
      window.removeEventListener('app-language-updated', syncLangFromStorage);
    };
  }, [lang]);

  const value = useMemo(() => ({ lang, setLang, t }), [lang, t]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => useContext(LanguageContext);
